import type { Web3Confirmation } from "@/cuties/blockchain/matic/Web3ProviderAdapter";
import Web3ProviderAdapter from "@/cuties/blockchain/matic/Web3ProviderAdapter";
import type { Web3Provider } from "@/cuties/blockchain/matic/Web3Provider";
import { Blockchain } from "@/cuties/model/pet/BlockchainId";
import type { EthereumConfig, EthereumContract } from "@/cuties/blockchain/ethereum/EthereumContractAddresses";
import type Web3 from "web3";
import CutiesApiFaucet from "@/app/cuties/blockchain/CutiesApiFaucet";

export type StakeContract = Extract<EthereumContract, "staking" | "farming">;

const APPROVE_AMOUNT = "1000000000000000000000000000000000000000";

export class EthereumStakingService {
    private adapter: Web3ProviderAdapter;

    constructor(
        private walletProvider: Web3Provider,
        private readonly contract: StakeContract,
        private readonly configuration: EthereumConfig
    ) {
        this.adapter = new Web3ProviderAdapter(walletProvider, Blockchain.Ethereum);
    }

    public async lp(): Promise<string> {
        return this.adapter.callContractMethod<string>({
            contractName: this.contract,
            methodName: "lp",
        });
    }

    public async availableTokens(address: string): Promise<string> {
        const web3 = await this.getWeb3();
        const lp = await this.lp();

        const amount = await this.adapter.callContractMethod<string>({
            contract: "erc20",
            address: lp,
            methodName: "balanceOf",
            methodArguments: [address],
        });

        return web3.utils.fromWei(amount, "ether");
    }

    public async allowance(address: string): Promise<string> {
        const web3 = await this.getWeb3();
        const contractAddr = this.configuration.contracts[this.contract];
        const lp = await this.lp();

        const amount = await this.adapter.callContractMethod<string>({
            contract: "erc20",
            address: lp,
            methodName: "allowance",
            methodArguments: [address, contractAddr],
        });

        return web3.utils.fromWei(amount, "ether");
    }

    public async approve(onConfirmation?: Web3Confirmation): Promise<string> {
        const web3 = await this.getWeb3();
        const contractAddr = this.configuration.contracts[this.contract];
        const lp = await this.lp();

        const amount = await this.adapter.sendContractMethod(
            {
                contract: "erc20",
                address: lp,
                methodName: "approve",
                methodArguments: [contractAddr, APPROVE_AMOUNT],
            },
            onConfirmation
        );

        return web3.utils.fromWei(amount, "ether");
    }

    public async totalStake(): Promise<string> {
        const web3 = await this.getWeb3();

        const amount = await this.adapter.callContractMethod<string>({
            contractName: this.contract,
            methodName: "totalSupply",
        });

        return web3.utils.fromWei(amount, "ether");
    }

    public async staked(address: string): Promise<string> {
        const web3 = await this.getWeb3();

        const amount = await this.adapter.callContractMethod<string>({
            contractName: this.contract,
            methodName: "balanceOf",
            methodArguments: [address],
        });

        return web3.utils.fromWei(amount, "ether");
    }

    public async earned(address: string): Promise<string> {
        const web3 = await this.getWeb3();

        const amount = await this.adapter.callContractMethod<string>({
            contractName: this.contract,
            methodName: "earned",
            methodArguments: [address],
        });

        return web3.utils.fromWei(amount, "ether");
    }

    /**
     * Stake BCUG token on a contract
     * @param value - human readable value like 0.1 BCUG
     * @param onConfirmation - confirmation callback, fired on each head block
     */
    public async stake(value: string, onConfirmation?: Web3Confirmation): Promise<string> {
        const web3 = await this.getWeb3();
        const amount = web3.utils.toWei(value, "ether");

        return this.adapter.sendContractMethod(
            {
                contractName: this.contract,
                methodName: "stake",
                methodArguments: [amount],
            },
            onConfirmation
        );
    }

    /**
     * Unstake BCUG token on a contract
     * @param onConfirmation - confirmation callback, fired on each head block
     */
    public async exit(onConfirmation?: Web3Confirmation): Promise<string> {
        return this.adapter.sendContractMethod(
            {
                contractName: this.contract,
                methodName: "exit",
            },
            onConfirmation
        );
    }

    /**
     * Unstake BCUG token on a contract
     * @param value - human readable value like 0.1 BCUG
     * @param onConfirmation - confirmation callback, fired on each head block
     */
    public async unstake(value: string, onConfirmation?: Web3Confirmation): Promise<string> {
        const web3 = await this.getWeb3();
        const amount = web3.utils.toWei(value, "ether");

        return this.adapter.sendContractMethod(
            {
                contractName: this.contract,
                methodName: "withdraw",
                methodArguments: [amount],
            },
            onConfirmation
        );
    }

    public async getRewardRate(): Promise<string> {
        const web3 = await this.getWeb3();
        const amount = await this.adapter.callContractMethod<string>({
            contractName: this.contract,
            methodName: "rewardRate",
        });

        return web3.utils.fromWei(amount, "ether");
    }

    public async getReward(onConfirmation?: Web3Confirmation): Promise<string> {
        return this.adapter.sendContractMethod(
            {
                contractName: this.contract,
                methodName: "getReward",
            },
            onConfirmation
        );
    }

    private async getWeb3(): Promise<Web3> {
        const Web3 = await CutiesApiFaucet.getWeb3();
        return new Web3();
    }
}
